import React, { useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import {
  originalPathState,
  userInfoState,
  platformState,
} from "../global-state";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Typography,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useApiCall from "../common/api-call";
// import Scope from "./scope";
import Developer from "./developer";
import Location from "./location";
import Room from "./room";
import DesktopNotificationSettingsView from "./desktop-notification";
import About from "./about";
import { useToast } from "../common/toast";
import Billing from "./billing";
import DesktopPermission from "./desktop-permission";
import PhonePermission from "./phone-permission";

const Setting = () => {
  const history = useHistory();
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const theme = useTheme();
  const [selectedPage, setSelectedPage] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [originalPath, setOriginalPath] = useRecoilState(originalPathState);
  const location = useLocation();
  const { successToast, warningToast, errorToast } = useToast();
  const { apiCall } = useApiCall();
  const [platform, setPlatform] = useRecoilState(platformState);

  const isSmallScreen = useMediaQuery("(max-width:768px)");

  const isApp = platform.source === "app";
  const isDesktop = platform.source === "desktop";

  useEffect(() => {
    if (location.hash) {
      setSelectedPage(location.hash.substring(1));
    } else if (!isSmallScreen && !location.hash) {
      const firstVisibleRoute = routes.find(
        (page) => page.hash !== "developer" || userInfo.setting.engAccount
      );
      if (firstVisibleRoute) {
        setSelectedPage(firstVisibleRoute.hash);
        history.replace(`/setting#${firstVisibleRoute.hash}`);
      }
    }
    setIsDefault(!location.hash);
  }, [location.hash, isSmallScreen, userInfo.setting.engAccount]);

  const routes = [
    { hash: "developer", title: "Developer", component: Developer },
    { hash: "location", title: "Location", component: Location },
    { hash: "room", title: "Room", component: Room },
    { hash: "billing", title: "Billing", component: Billing },
    ...(isApp
      ? [
          {
            hash: "phone-permission",
            title: "Phone Permission",
            component: PhonePermission,
          },
        ]
      : []),
    ...(isDesktop
      ? [
          {
            hash: "desktop-permission",
            title: "Desktop Permission",
            component: DesktopPermission,
          },
          {
            hash: "notification",
            title: "Notification",
            component: DesktopNotificationSettingsView,
          },
        ]
      : []),
    { hash: "about", title: "About", component: About },
  ];

  const handleNavigation = (hash) => {
    setIsDefault(false);
    setSelectedPage(hash);
    if (isSmallScreen) {
      history.replace(`/setting#${hash}`);
    } else {
      history.replace(`/setting#${hash}`);
    }
  };

  const handleUserLogout = async () => {
    setOriginalPath(location.hash);
    localStorage.removeItem("filterParams-taskflow");
    localStorage.removeItem("filterParams-recurring");
    history.push("/user-logout");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: isSmallScreen ? "0" : "16px",
        overflow: "hidden",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        backgroundColor: isSmallScreen ? "transparent" : "background.paper",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          height: "100%",
          alignItems: "flex-start",
          gap: 2,
          position: "relative",
        }}
      >
        {/* Sidebar */}
        {(!isSmallScreen || (isSmallScreen && isDefault)) && (
          <Box
            sx={{
              width: isSmallScreen ? "100%" : "35%",
              maxWidth: isSmallScreen ? "100%" : 375,
              minHeight: "80vh",
              height: "100%",
              position: isSmallScreen ? "relative" : "sticky",
              top: 0,
              flexShrink: 0,
              borderRadius: 3,
              p: 2,
              backgroundColor: isSmallScreen
                ? "transparent"
                : "background.default",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
                p: 2,
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mt: 1,
                  bgcolor: theme.palette.background.avatar,
                  fontSize: "2rem",
                  fontWeight: 600,
                  boxShadow: "none",
                }}
              >
                {userInfo.name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.gray,
                }}
              >
                {userInfo.name}
              </Typography>
            </Box>
            <List>
              {routes
                .filter((page) => {
                  return (
                    page.hash !== "developer" || userInfo.setting.engAccount
                  );
                })
                .map((page, index) => (
                  <ListItem
                    button
                    key={index}
                    onClick={() => handleNavigation(page.hash)}
                    sx={{
                      textAlign: "center",
                      mb: 1,
                      borderRadius: "12px",
                      backgroundColor:
                        selectedPage === page.hash
                          ? theme.palette.action.selected
                          : "background.card",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemText primary={page.title} />
                  </ListItem>
                ))}
              <ListItem
                button
                onClick={() => {
                  handleUserLogout();
                }}
                sx={{
                  textAlign: "center",
                  mb: 1,
                  borderRadius: "12px",
                  backgroundColor: "background.card",
                  color: "#FF3B30",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "rgba(255, 59, 48, 0.2)",
                  },
                }}
              >
                <ListItemText
                  primary="Sign out"
                  sx={{
                    "& .MuiListItemText-primary": {
                      color: "#FF3B30",
                      fontWeight: 600,
                    },
                  }}
                />
              </ListItem>
            </List>
          </Box>
        )}

        {/* Content */}
        {(!isSmallScreen || (isSmallScreen && !isDefault)) && (
          <Box
            sx={{
              flexGrow: 1,
              height: "100%",
              overflowY: "auto",
              borderRadius: 3,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              minHeight: "80vh",
              backgroundColor: isSmallScreen
                ? "transparent"
                : "background.default",
            }}
          >
            <Box sx={{ height: "100%" }}>
              {selectedPage === "developer" && userInfo.setting.engAccount && (
                <Developer />
              )}
              {selectedPage === "location" && <Location />}
              {selectedPage === "room" && <Room />}
              {selectedPage === "billing" && <Billing />}
              {selectedPage === "notification" && (
                <DesktopNotificationSettingsView />
              )}
              {selectedPage === "phone-permission" && isApp && (
                <PhonePermission />
              )}
              {selectedPage === "desktop-permission" && isDesktop && (
                <DesktopPermission />
              )}
              {selectedPage === "about" && <About />}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Setting;
